import { FormEvent, useCallback, useEffect, useState } from "react"

import { login, winLogin } from "providers/A12n"

import { AlertDlg } from "components/AlertDlg"
import { ApiError } from "api/http/utils"
//
import { Button } from "ui/buttons/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import CloseIcon from "@material-ui/icons/Close"
import Collapse from "@material-ui/core/Collapse"
import { ReactComponent as Email } from "assets/icon-email.svg"
import IconButton from "@material-ui/core/IconButton"
import { InputAdornment } from "@material-ui/core"
import { Loading } from "components/Loading"
import { ReactComponent as LogoLarge } from "assets/logos/logo-halogen-colored.svg"
import { ReactComponent as Password } from "assets/icon-password.svg"
import TextField from "@material-ui/core/TextField"
import { checkActiveUser } from "api/user"
import { css } from "@emotion/css"
import { useHistory } from "react-router-dom"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"

const styles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  card: css`
    max-width: 256px;
    width: 256px;
  `,
  logo: css`
    > svg {
      height: 40px;
      width: 137px;
    }
  `,
  formWrap: css`
    margin-top: var(--spacing-xxl-6);
  `,
  form: css`
    padding-top: var(--spacing-m);
    display: flex;
    flex-direction: column;

    .form_el {
      width: 100%;
    }
    .submit_btn {
      margin-top: var(--spacing-m);
    }
  `,
  errorWrap: css`
    padding-top: var(--spacing-xxl-2);
  `,
  alertIcon: css`
    padding: 0;
    font-size: 1.625rem;
  `,
  loginIcon: css`
    color: var(--grey);
    width: 28px;
    height: 24px;
  `,
  inputLabel: css`
    padding-left: var(--spacing-xxl-2);
  `,
  input: css`
    padding-left: 0;
  `,
  shrinked: css`
    padding-left: 0;
  `,
}

type LoginValues = {
  username: string
  password: string
}

const initialValues: LoginValues = {
  username: "",
  password: "",
}

const nope = (s: string) => !s.trim()

export function Login() {
  const { t } = useI18n()
  const search = useLocationSearch()
  const code = search.get("code")
  const [values, setValues] = useState<LoginValues>(initialValues)
  const [error, setError] = useState<string | undefined>()
  const [pending, setPending] = useState<boolean>(false)
  const [shrink, setShrink] = useState<Record<string, boolean>>(() => ({
    username: false,
    password: false,
  }))
  const history = useHistory()

  const onChange = useCallback(
    (evt) => {
      const {
        target: { name, value },
      } = evt
      setError(undefined)
      setValues((s) => ({ ...s, [name]: value }))
    },
    [setValues],
  )

  const handleSubmit = (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    setPending(true)
    login(values)
      .then(({ accessToken }) => {
        return checkActiveUser(accessToken)
      })
      .then(() => {
        setPending(false)
        history.push("/")
      })

      .catch((_error) => {
        const msg = (_error as ApiError).message
        let error_key = "error.unknown"
        if (msg === "invalid_grant") {
          error_key = "login.wrong_credentials"
        } else if (
          msg === "cannot.login.user.that.belongs.to.not.active.agency"
        ) {
          error_key = "cannot.login.user.that.belongs.to.not.active.agency"
        }
        setError(error_key)
        setPending(false)
      })
  }

  const handleWinLogin = useCallback(
    (code: string) => {
      setPending(true)
      winLogin(code)
        .then(({ accessToken }) => {
          return checkActiveUser(accessToken)
        })
        .then(() => {
          setPending(false)
          history.push("/")
        })
        .catch((_error) => {
          const msg = (_error as ApiError).message
          let error_key = "error.unknown"
          setError(msg || error_key)
          setPending(false)
        })
    },
    [history],
  )

  useEffect(() => {
    if (!!code) {
      handleWinLogin(code)
    }
  }, [code, handleWinLogin])

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <LogoLarge />
        </div>
        {pending && !!code ? (
          <Loading />
        ) : (
          <div className={styles.formWrap}>
            <h4 className="HeadlineH4BlackRegular">{t("account.login")}</h4>
            <Collapse in={!!error}>
              <div className={styles.errorWrap}>
                <AlertDlg
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="medium"
                      onClick={() => setError(undefined)}
                      classes={{ root: styles.alertIcon }}
                    >
                      <CloseIcon />
                    </IconButton>
                  }
                >
                  {t(error!)}
                </AlertDlg>
              </div>
            </Collapse>
            {!process.env.REACT_APP_HIDE_FORM_LOGIN ? (
              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className="form_el">
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    size="medium"
                    name="username"
                    label={"Email"}
                    onFocus={() => setShrink((s) => ({ ...s, username: true }))}
                    onBlur={() =>
                      setShrink((s) => ({ ...s, username: !!values.username }))
                    }
                    onChange={onChange}
                    value={values.username}
                    required
                    onAnimationStart={(e: any) => {
                      setShrink((s) => ({
                        ...s,
                        username: e.animationName === "mui-auto-fill",
                      }))
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position={"start"}>
                          <Email className={styles.loginIcon} />
                        </InputAdornment>
                      ),
                      classes: { input: styles.input },
                    }}
                    InputLabelProps={{
                      shrink: shrink.username || !!values.username,
                      className: styles.inputLabel,
                      classes: { shrink: styles.shrinked },
                    }}
                  />
                </div>
                <div className="form_el">
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    size="medium"
                    type="password"
                    name="password"
                    label={"Password"}
                    onFocus={() => setShrink((s) => ({ ...s, password: true }))}
                    onBlur={() =>
                      setShrink((s) => ({ ...s, password: !!values.password }))
                    }
                    onChange={onChange}
                    value={values.password}
                    required
                    onAnimationStart={(e: any) => {
                      setShrink((s) => ({
                        ...s,
                        password: e.animationName === "mui-auto-fill",
                      }))
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position={"start"}>
                          <Password className={styles.loginIcon} />
                        </InputAdornment>
                      ),
                      classes: { input: styles.input },
                    }}
                    InputLabelProps={{
                      shrink: shrink.password || !!values?.password,
                      className: styles.inputLabel,
                      classes: { shrink: styles.shrinked },
                    }}
                  />
                </div>
                <div className="form_el submit_btn">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={
                      pending || nope(values.username) || nope(values.password)
                    }
                    startIcon={
                      pending && <CircularProgress size={20} color="inherit" />
                    }
                  >
                    {t("account.login")}
                  </Button>
                </div>
              </form>
            ) : null}
            <form
              className={styles.form}
              action={`${window.location.origin}/auth/h3login/auth`}
              method="GET"
              noValidate
            >
              <input type="hidden" name="client_id" value="login" />
              {/* <input type="hidden" name="scope" value="openid profile email" /> */}
              <input type="hidden" name="kc_idp_hint" value="saml" />
              <input
                type="hidden"
                name="redirect_uri"
                value={`${window.location.origin}/api/login`}
              />
              <input type="hidden" name="response_mode" value="form_post" />
              <input type="hidden" name="response_type" value="code" />
              {/* <input type="hidden" name="nonce" value="abc12345" /> */}
              <Button type="submit" variant="contained" color="primary">
                {t("windows.login")}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
